<template>
    <div class="campaign__container">
        <side-menu-header :role="user.role"></side-menu-header>
        <div class="campaign__body">
            <h1 class="campaign__header smooth-show" v-if="nthCampaign">Hi {{ user.user }}, This is your {{ nthCampaign }} campaign!</h1>
            <h3 class="campaign__info">Let's start with basic info</h3>

            <img v-if="!allCategories" class="header__car" src="./../../assets/header_car.png" style="position: absolute; top:20px; right:0; width: 30vw;" />

            <div class="row basic__info">
                <div class="pair">
                    <label class="pair__label">What is your campaign name?</label>
                    <input type="text" placeholder="eg. Food promotion" v-model="campaign.name" />
                    <label class="error" v-if="errors && errors.name">{{ errors.name }}</label>
                </div>
                <div class="pair" v-if="user.role !== 'advertiser'">
                    <label class="pair__label">What is your clients name?</label>
                    <input type="text" placeholder="eg. my client 1" v-model="campaign.client" />
                    <label class="error" v-if="errors && errors.client">{{ errors.client }}</label>
                </div>
            </div>

            <div class="campaign__category">
                <label class="pair__label">What is campaign category?</label>
                <div class="category__list">
                    <p v-for="(item) in categories" :key="item.id" :class="{'selected': item.id === campaign.category}" @click="selectCategory(item.id)">{{ item.name }}</p>
                    <p class="all__categories" @click="allCategories=!allCategories">{{ allCategories ? 'View less' : 'View all' }}</p>
                </div>
                <label class="error" v-if="errors && errors.category">{{ errors.category }}</label>
            </div>

            <div class="budget__limit">
                <label class="pair__label">Let's see your budget limit</label>
                <div class="budget__holder">
                    <div class="pair">
                        <label class="pair__label">Set by the cost</label>
                        <input type="number" @keydown="validateLimit" @input="calculateTotals('spend_limit')" v-model="campaign.spend_limit" />
                    </div>
                    <p>OR</p>
                    <div class="pair">
                        <label class="pair__label">Set by the impressions</label>
                        <input type="number" @keydown="validateLimit" @input="calculateTotals('show_limit')" v-model="campaign.show_limit" />
                    </div>
                </div>
                <label class="error" v-if="errors && errors.limit">{{ errors.limit }}</label>
            </div>

            <div class="schedule__campaign">
                <h1 class="campaign__info">Let's schedule your campaign</h1>
                <div class="row schedule__row">
                    <div class="calendar__section">
                        <div class="pair">
                            <label class="pair__label">Start date</label>
                            <v-date-picker mode="single" :masks="{ input: ['MM-DD-YYYY']}" :input-props='{class: "datepicker"}' v-model='campaign.start_date'/>
                        </div>
                        <div class="pair">
                            <label class="pair__label">End date</label>
                            <v-date-picker mode="single" :masks="{ input: ['MM-DD-YYYY']}" :input-props='{class: "datepicker"}' v-model='campaign.end_date'/>
                        </div>
                    </div>

                    <div class="time__section">
                        <div class="pair">
                            <label class="pair__label">Start time</label>
                            <el-time-picker
                                v-model="campaign.start_time"
                                selectableRange="00:00 - 12:59"
                                format="hh:mm A"
                                value-format="HH:mm"
                                placeholder="00:00">
                            </el-time-picker>
                        </div>
                        <div class="pair">
                            <label class="pair__label">End time</label>
                            <el-time-picker
                                v-model="campaign.end_time"
                                selectableRange="00:00 - 12:59"
                                format="hh:mm A"
                                value-format="HH:mm"
                                placeholder="00:00">
                            </el-time-picker>
                        </div>
                    </div>
                </div>
                <label class="error" v-if="errors && errors.time">{{ errors.time }}</label>
            </div>

            <div class="campaign__location">
                <h1 class="campaign__info">Select location for the campaign</h1>
                <div class="row location__row">
                    <div class="location__details">
                        <div class="google-type-container">
                            <div class="google-type-holder" @click="mapTypeChange('zipcode')" :class="{'active-google-type-holder' : mapTypeActive === 'zipcode'}">
                                <div class="google-type-text" id="google-type-text">Select location by Zipcode</div>
                                <zipcode-icon></zipcode-icon>
                            </div>

                            <div class="google-type-holder" @click="mapTypeChange('geofence')" :class="{'active-google-type-holder' : mapTypeActive === 'geofence'}">
                                <div class="google-type-text" id="google-type-text">Select location by Geofence</div>
                                <geofence-icon></geofence-icon>
                            </div>
                        </div>

                        <label class="pair__label">Search location by address</label>
                        <GmapAutocomplete :value="campaign.address" @place_changed="addressChanged" :disabled="mapTypeActive==='zipcode'"></GmapAutocomplete>
                        <label class="error" v-if="errors && errors.address">{{ errors.address }}</label>

                        <p>OR</p>

                        <label class="pair__label">Search location by zip code</label>
                        <div class="zip__input">
                            <p v-for="zip in campaign.ZIP_codes" :key="zip">
                                {{ zip }}
                                <svg @click="removeZIP(zip)" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </p>
                            <input type="text" v-on:keydown="checkKey" v-model="zipCode" :disabled="mapTypeActive==='geofence'" />
                        </div>
                        <label class="error" v-if="errors && errors.zip">{{ errors.zip }}</label>

                        <label class="upload__label" for="upload">
                            <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.24399 6.78613L8.11899 3.91113V13.2111C8.11511 13.3228 8.13328 13.4342 8.17244 13.5389C8.2116 13.6435 8.271 13.7395 8.34723 13.8212C8.42347 13.9029 8.51505 13.9688 8.61676 14.0151C8.71847 14.0614 8.8283 14.0873 8.93999 14.0911H8.99499C9.10668 14.095 9.21804 14.0768 9.32271 14.0377C9.42738 13.9985 9.52331 13.9391 9.60503 13.8629C9.68674 13.7867 9.75264 13.6951 9.79896 13.5934C9.84528 13.4917 9.87112 13.3818 9.87499 13.2701V3.91113L12.725 6.75613C12.8853 6.92201 13.1044 7.01806 13.335 7.02349C13.5656 7.02892 13.7891 6.94328 13.957 6.78513L13.986 6.75613C14.1415 6.58872 14.228 6.36866 14.228 6.14013C14.228 5.91161 14.1415 5.69155 13.986 5.52413L9.61499 1.15313C9.44758 0.997586 9.22751 0.911133 8.99899 0.911133C8.77047 0.911133 8.5504 0.997586 8.38299 1.15313L4.01199 5.52413C3.85644 5.69155 3.76999 5.91161 3.76999 6.14013C3.76999 6.36866 3.85644 6.58872 4.01199 6.75613C4.17213 6.92215 4.39124 7.01838 4.62183 7.024C4.85243 7.02962 5.07595 6.94416 5.24399 6.78613Z" fill="white"/>
                                <path d="M16.539 9.78225C16.4273 9.77837 16.3159 9.79654 16.2113 9.8357C16.1066 9.87486 16.0107 9.93425 15.9289 10.0105C15.8472 10.0867 15.7813 10.1783 15.735 10.28C15.6887 10.3817 15.6629 10.4916 15.659 10.6032V14.5632C15.6538 14.6393 15.6213 14.7108 15.5674 14.7647C15.5136 14.8186 15.442 14.8511 15.366 14.8563H2.63399C2.55797 14.8511 2.4864 14.8186 2.43253 14.7647C2.37866 14.7108 2.34613 14.6393 2.34099 14.5632V10.6633C2.34486 10.5516 2.3267 10.4402 2.28754 10.3355C2.24837 10.2309 2.18898 10.1349 2.11274 10.0532C2.03651 9.9715 1.94492 9.9056 1.84321 9.85928C1.74151 9.81296 1.63168 9.78712 1.51999 9.78325H1.45999C1.34383 9.78108 1.22842 9.80236 1.12067 9.84581C1.01292 9.88927 0.915046 9.95401 0.832894 10.0362C0.750741 10.1183 0.686003 10.2162 0.642548 10.3239C0.599093 10.4317 0.577813 10.5471 0.579985 10.6633V14.5632C0.596298 15.1028 0.817939 15.6159 1.19966 15.9976C1.58138 16.3793 2.0944 16.6009 2.63399 16.6172H15.366C15.9056 16.6009 16.4186 16.3793 16.8003 15.9976C17.182 15.6159 17.4037 15.1028 17.42 14.5632V10.6633C17.4223 10.5469 17.4011 10.4313 17.3576 10.3234C17.3142 10.2155 17.2494 10.1174 17.1671 10.0351C17.0848 9.95286 16.9868 9.88806 16.8788 9.8446C16.7709 9.80115 16.6553 9.77994 16.539 9.78225Z" fill="white"/>
                            </svg>
                            Upload multiple zip codes
                            <input type="file" id="upload" @change="excellUploaded" v-show="false" accept=".xlsx, .xls, .csv" />
                        </label>
                        <label class="upload__label__note">*Upload files in Excel & csv format</label>
                    </div>

                    <div class="location__map">
                        <GmapMap
                            ref="mapRef"
                            :center="{lat: campaign.lat, lng: campaign.lon}"
                            :zoom="10"
                            :options="{mapTypeControl: false, streetViewControl: false, styles, zoomControl: false, gestureHandling: 'cooperative'}"
                            style="width: 100%; height: 350px;"
                            @click="mapClicked"
                        >
                            <GmapCircle
                                ref="mapCircle"
                                v-if="campaign.address && mapTypeActive === 'geofence'"
                                :center="{lat:campaign.lat, lng:campaign.lon}"
                                :radius="campaign.distance"
                                :clickable="true"
                                :draggable="true"
                                :editable="true"
                                :options="{strokeColor: '#102130', strokeWeight: 2, fillColor: 'rgb(223, 242, 255)', fillOpacity: .5}"
                            />
                            <div v-if="mapTypeActive == 'zipcode'">
                                <GmapPolygon
                                v-for="(polygon, index) in polygons"
                                v-bind:key="index"
                                :path.sync="polygon"
                                :options="{strokeColor: '#102130', strokeWeight: 2, fillColor: 'rgb(223, 242, 255)', fillOpacity: .5}"
                                />
                            </div>
                        </GmapMap>
                    </div> <!-- Map -->
                </div>
            </div>

            <div class="car__type">
                <h1 class="campaign__info">What is your car type ?</h1>
                <div class="car__type__list">
                    <div class="car__type__item" @click="selectType(schema.car_types[0].id)" :class="{'active': campaign.car_types.includes(schema.car_types[0].id)}">
                        <small-car :selected="campaign.car_types.includes(schema.car_types[0].id)"></small-car>
                        <p>Small</p>
                    </div>
                    <div class="car__type__item" @click="selectType(schema.car_types[1].id)" :class="{'active': campaign.car_types.includes(schema.car_types[1].id)}">
                        <medium-car :selected="campaign.car_types.includes(schema.car_types[1].id)"></medium-car>
                        <p>Medium</p>
                    </div>
                    <div class="car__type__item" @click="selectType(schema.car_types[2].id)" :class="{'active': campaign.car_types.includes(schema.car_types[2].id)}">
                        <luxury-car :selected="campaign.car_types.includes(schema.car_types[2].id)"></luxury-car>
                        <p>Luxury</p>
                    </div>
                    <div class="car__type__item" @click="selectType(schema.car_types[3].id)" :class="{'active': campaign.car_types.includes(schema.car_types[3].id)}">
                        <truck :selected="campaign.car_types.includes(schema.car_types[3].id)"></truck>
                        <p>Truck</p>
                    </div>
                    <div class="car__type__item select__all" @click="selectAllTypes()">
                        <p>Select All</p>
                    </div>
                </div>
                <label class="error" v-if="errors && errors.car_types">{{ errors.car_types }}</label>
            </div>

            <div class="add__campaign__creative">
                <h1 class="campaign__info">Add campaign creative</h1>
                <div class="row">
                    <div class="info__holder">
                        <div class="upload__container" @dragenter="dragEnter()" @dragleave="dragLeave()" :class="{'drag__enter': drag}">
                            <div class="drag__img">
                                <img src="./../../assets/upload_cards.svg" ref="dragIMG" />
                            </div>
                             <canvas id="dragImgCanvas"></canvas>
                            <p>Drag and drop to upload</p>
                            <button>
                                <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.24399 6.78613L8.11899 3.91113V13.2111C8.11511 13.3228 8.13328 13.4342 8.17244 13.5389C8.2116 13.6435 8.271 13.7395 8.34723 13.8212C8.42347 13.9029 8.51505 13.9688 8.61676 14.0151C8.71847 14.0614 8.8283 14.0873 8.93999 14.0911H8.99499C9.10668 14.095 9.21804 14.0768 9.32271 14.0377C9.42738 13.9985 9.52331 13.9391 9.60503 13.8629C9.68674 13.7867 9.75264 13.6951 9.79896 13.5934C9.84528 13.4917 9.87112 13.3818 9.87499 13.2701V3.91113L12.725 6.75613C12.8853 6.92201 13.1044 7.01806 13.335 7.02349C13.5656 7.02892 13.7891 6.94328 13.957 6.78513L13.986 6.75613C14.1415 6.58872 14.228 6.36866 14.228 6.14013C14.228 5.91161 14.1415 5.69155 13.986 5.52413L9.61499 1.15313C9.44758 0.997586 9.22751 0.911133 8.99899 0.911133C8.77047 0.911133 8.5504 0.997586 8.38299 1.15313L4.01199 5.52413C3.85644 5.69155 3.76999 5.91161 3.76999 6.14013C3.76999 6.36866 3.85644 6.58872 4.01199 6.75613C4.17213 6.92215 4.39124 7.01838 4.62183 7.024C4.85243 7.02962 5.07595 6.94416 5.24399 6.78613Z" fill="white"/>
                                    <path d="M16.539 9.78225C16.4273 9.77837 16.3159 9.79654 16.2113 9.8357C16.1066 9.87486 16.0107 9.93425 15.9289 10.0105C15.8472 10.0867 15.7813 10.1783 15.735 10.28C15.6887 10.3817 15.6629 10.4916 15.659 10.6032V14.5632C15.6538 14.6393 15.6213 14.7108 15.5674 14.7647C15.5136 14.8186 15.442 14.8511 15.366 14.8563H2.63399C2.55797 14.8511 2.4864 14.8186 2.43253 14.7647C2.37866 14.7108 2.34613 14.6393 2.34099 14.5632V10.6633C2.34486 10.5516 2.3267 10.4402 2.28754 10.3355C2.24837 10.2309 2.18898 10.1349 2.11274 10.0532C2.03651 9.9715 1.94492 9.9056 1.84321 9.85928C1.74151 9.81296 1.63168 9.78712 1.51999 9.78325H1.45999C1.34383 9.78108 1.22842 9.80236 1.12067 9.84581C1.01292 9.88927 0.915046 9.95401 0.832894 10.0362C0.750741 10.1183 0.686003 10.2162 0.642548 10.3239C0.599093 10.4317 0.577813 10.5471 0.579985 10.6633V14.5632C0.596298 15.1028 0.817939 15.6159 1.19966 15.9976C1.58138 16.3793 2.0944 16.6009 2.63399 16.6172H15.366C15.9056 16.6009 16.4186 16.3793 16.8003 15.9976C17.182 15.6159 17.4037 15.1028 17.42 14.5632V10.6633C17.4223 10.5469 17.4011 10.4313 17.3576 10.3234C17.3142 10.2155 17.2494 10.1174 17.1671 10.0351C17.0848 9.95286 16.9868 9.88806 16.8788 9.8446C16.7709 9.80115 16.6553 9.77994 16.539 9.78225Z" fill="white"/>
                                </svg>
                                {{ imgUploaded ? 'Upload Another Creative' : 'Upload Creative' }}
                            </button>
                            <input type="file" @change="imageUploaded" style="opacity: 0; width: 100%;height: 100%;position: absolute;top:0;left:0;" accept=".mp4, .jpeg, .jpg, .png, .gif, .flv, .mov, .avi" />
                            <label class="error" v-if="errors && errors.image && !imgUploaded">{{ errors.image }}</label>
                        </div>
                        <div class="info__container">
                            <div>
                                <label>Image format</label>
                                <label>Jpeg, Png, Gif</label>
                                <label>Size limit 4MB</label>
                            </div>

                            <div>
                                <label>Video format</label>
                                <label>Avi, Mov, Mp4, Flv</label>
                                <label>Size limit 10MB</label>
                                <label>Duration limit 30sec</label>
                            </div>
                        </div>
                        <!-- Slider  -->

                        <div class="slider__container">
                            <p>8</p>
                            <vue-slider v-model="campaign.duration" :min="8" :max="30" @change="durationChanged" />
                            <p>30</p>
                        </div>
                    </div>

                    <div class="preview__holder">
                        <div>
                            <img src="./../../assets/adway_demo_car.png" />
                            <img class="overflow__img" ref="overflowIMG" />
                            <video id="videoPreviewImg" style="display: none; opacity: 0"></video>
                            <canvas id="prevImgCanvas"></canvas>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="campaign__creative" id="campaign__creative">
            <div class="total__budget">
                <p>TOTAL BUDGET</p>
                <h3>${{ total_budget }} @ ${{ CPM }} CPM</h3>
            </div>
            <div class="total__impressions">
                <p>TOTAL IMPRESSIONS</p>
                <h3>{{ total_impressions }} @ ${{ CPM }} CPM</h3>
            </div>
            <div class="buttons">
                <button @click="cancelCampaign()">Cancel</button>
                <button @click="startCampaign()" style="z-index: 999;">
                    <clip-loader color="#2EA3FF" size="15px" :loading="activeRequest" />
                    {{ activeRequest ? 'Saving...' : 'Start your campaign' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import SideMenuHeader from './../design/SideMenuHeader.vue'
import SmallCar from '@components/svg/SmallCar.vue'
import MediumCar from '@components/svg/MediumCar.vue'
import LuxuryCar from '@components/svg/LuxuryCar.vue'
import Truck from '@components/svg/Truck.vue'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import HTTPRequest from '@utils/HttpRequest.js'
import TimePicker from '@components/partials/TimePicker.vue'
import XLSX from 'xlsx'
import styles from '@utils/mapStylesDark.js'
import Config from '@utils/Config.js'
import {gmapApi} from 'vue2-google-maps'
import {reverseGeocode, InitZoomControl, InitTypeControl, InitTrafficVolume, InitResizeIcons } from '@utils/MapsHelper.js'
import Datepicker from 'vuejs-datepicker'
import { mapGetters } from 'vuex'
import toast from '@utils/toast'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'
import GeofenceIcon from '@svg/Geofence'
import ZipcodeIcon from '@svg/Zipcode'
import $ from 'jquery'
import initStickyTotals from '@utils/totals'
import moment from 'moment'

let geocoder = null

export default {
    data () {
        return {
            campaignCategories: [],
            allCategories: false,
            initialCampaign: {},
            campaign: {
                name: null,
                client: null,
                category: null,
                lat: 34.0201465,
                lon: -118.2443478,
                address: '1925 Compton Ave, Los Angeles, CA 90011, USA',
                distance: 10000,
                duration: 8,
                start_date: null,
                end_date: null,
                start_time: null,
                end_time: null,
                spend_limit: null,
                show_limit: null,
                car_types: [],
                ZIP_codes: [],
                active: 'Y'
            },
            zipCode: null,
            drag: false,
            styles,
            polygons: [],
            map: null,
            content: null,
            mapTypeActive: 'geofence',
            imgUploaded: false,
            trafficVolume: {
                minPeople: '',
                maxPeople: '',
                avgPeople: ''
            },
            campaigns: null,
            errors: null,
            total_budget: 0,
            total_impressions: 0,
            activeRequest: false,
            lastCenter: {lat: null, lng: null},
            startTime: null,
            endTime: null,
            bounds: null,
            CPM: 0
        }
    },
    methods: {
        selectCategory (category) {
            if (this.campaign.category === category) {
                this.campaign.category = null
            } else {
                this.campaign.category = category
            }
        },
        selectType (type) {
            if (this.campaign.car_types.includes(type)) {
                const index = this.campaign.car_types.indexOf(type)
                this.campaign.car_types.splice(index, 1)
            } else {
                this.campaign.car_types.push(type)
            }
        },
        selectAllTypes () {
            if (this.campaign.car_types.length === 4) {
                this.campaign.car_types = []
            } else {
                this.schema.car_types.map(type=>this.campaign.car_types.push(type.id))
            }
        },

        // Prevent user from using special chars and 'e' in the limits input fields
        validateLimit(e){
            var invalidChars = ["-", "+", "e"]
            if(invalidChars.includes(e.key)){
                e.preventDefault()
            }
        },

        durationChanged(value) {
            if(this.campaign.spend_limit){
                this.calculateTotals('spend_limit')
            } else if(this.campaign.show_limit){
                this.calculateTotals('show_limit')
            }
        },

        // Calculate the totals from the given distance & cpm and update the sticky totals footer
        calculateTotals(attr){
            if(!this.campaign.spend_limit && !this.campaign.show_limit){
                this.total_impressions = 0
                this.total_budget = 0
                return
            }

            this.CPM = this.evalCPM()

            if(attr === 'show_limit'){
                this.campaign.spend_limit = null
                this.total_impressions = parseInt(this.campaign.show_limit)
                this.total_budget = ((this.total_impressions/1000)*this.CPM).toFixed(2).toLocaleString()
            } else {
                this.campaign.show_limit = null
                this.total_budget = parseInt(this.campaign.spend_limit)
                this.total_impressions = parseInt((this.total_budget/this.CPM)*1000).toLocaleString()
            }

            this.CPM = this.CPM.toFixed(2)
        },

        // Evaluate the given formula from backend to calculate the CPM for totals
        evalCPM () {
            let cpm = this.user.CPM_formula
            cpm = cpm.replace(/CPM_base/g, this.user.CPM_base)
            cpm = cpm.replace(/duration/g, this.campaign.duration)
            return eval(cpm)
        },

        // Validate the fields, then send request to create campaign
        startCampaign () {
            if(this.activeRequest){
                return
            }

            this.activeRequest = true
            if(!this.validateFields()){
                toast.invalidInput()
                this.activeRequest = false
                return
            }
            this.errors = {}

            var url = '/set/campaign'
            const formData = new FormData()

            // Convert the dates into strings
            this.campaign.start_date = this.customFormatter(this.campaign.start_date)
            this.campaign.end_date = this.customFormatter(this.campaign.end_date)

            if(this.campaign.start_time.toString().length > 5){
                this.campaign.start_time = moment(this.campaign.start_time).format('HH:mm')
            }
            if(this.campaign.end_time.toString().length > 5){
                this.campaign.end_time = moment(this.campaign.end_time).format('HH:mm')
            }

            // Deconstruct campaign data into the needed body object
            const data = { ...this.campaign }

            const json = {
                data,
                API_KEY: Config.api_key,
                code: this.token.token,
                user: this.user.user
            }

            formData.append('json', JSON.stringify(json))
            formData.append('content', this.content)

            this.request(url, 'POST', formData).then((response) => {
                this.activeRequest = false
                if (response.code === 112) {
                    this.checkTokenConditions(this.startCampaign)
                }
                if(response.result === "failure"){
                    toast.failedInsert(response.message)
                } else if(response.result === "success") {
                    toast.campaignCreated()
                    this.$router.push({name: 'Dashboard'})
                }
            }).catch((error) => {
                toast.failedInsert(error)
                this.activeRequest = false
            })
        },

        // Check if user has made changes & prompt popup or just go to dashboard
        cancelCampaign () {
            if(JSON.stringify(this.initialCampaign) !== JSON.stringify(this.campaign)){
                toast.confirmCancel(() => this.$router.push({name: 'Dashboard'}))
            } else {
                this.$router.push({name: 'Dashboard'})
            }
        },

        // Validate the input fields in the create campaign form
        validateFields() {
            this.errors = {}
            var valid = true

            if(!this.campaign.name){
                this.errors.name = 'This field cannot be empty'
                valid = false
            } else {
                this.errors.name = null
            }
            if(!this.campaign.client && this.user.role !== 'advertiser'){
                this.errors.client = 'This field cannot be empty'
                valid = false
            } else {
                this.errors.client = null
            }
            if(!this.campaign.category){
                this.errors.category = 'Please select a category'
                valid = false
            } else {
                this.errors.category = null
            }
            if(!this.campaign.show_limit && !this.campaign.spend_limit){
                this.errors.limit = 'Please set limit'
                valid = false
            } else {
                this.errors.limit = null
            }
            if(!this.campaign.start_date || !this.campaign.end_date || !this.campaign.start_time || !this.campaign.end_time){
                this.errors.time = 'Please pick date and time'
                valid = false
            } else {
                this.errors.time = null
            }
            if(!this.campaign.address && this.mapTypeActive === 'geofence'){
                this.errors.address = 'Please select an address'
                valid = false
            } else {
                this.errors.address = null
            }
            if(this.campaign.ZIP_codes.length === 0 && this.mapTypeActive === 'zipcode'){
                this.errors.zip = 'Please select at least one zipcode'
                valid = false
            } else {
                this.errors.zip = null
            }
            if(this.campaign.car_types.length === 0){
                this.errors.car_types = 'Please select at least one car type'
                valid = false
            } else {
                this.errors.car_types = null
            }
            if(!this.imgUploaded && !this.campaign.content_url){
                valid = false;
                this.errors.image = 'Please upload an image'

            }

            return valid
        },

        // Filter the excell and update the input
        excellUploaded (event) {
            const oFile = event.target.files[0]
            const self = this

            const reader = new FileReader()

            reader.onload = function (e) {
                let data = e.target.result
                data = new Uint8Array(data)
                const workbook = XLSX.read(data, { type: 'array' })
                const result = {}
                workbook.SheetNames.forEach(function (sheetName) {
                    const roa = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 })
                    if (roa.length) result[sheetName] = roa
                })

                result.Sheet1.map(zip => {
                    if(!isNaN(zip[0]) && parseInt(zip[0]) < 99999){
                        self.campaign.ZIP_codes.push(zip[0])
                    }
                })

                if(self.mapTypeActive === 'geofence'){
                    self.mapTypeActive = 'zipcode'
                    self.getBoundaries()
                    self.getTrafficVolume()
                }
            }
            reader.readAsArrayBuffer(oFile)
        },
         reloadRandomFrame () {
             var video = document.getElementById("videoPreviewImg");

            if (!isNaN(video.duration)) {
                var rand = Math.round(Math.random() * video.duration * 1000) + 1;
                console.log("load at " + rand + " millis");
                video.currentTime = rand / 1000;
            }
        },
        clearCanvas (){
             var canvas = document.getElementById("prevImgCanvas")
            const context = canvas.getContext('2d')
            context.clearRect(0, 0, canvas.width, canvas.height)

            var dragcanvas = document.getElementById("dragImgCanvas")
            const context2 = dragcanvas.getContext('2d')
            context2.clearRect(0, 0, dragcanvas.width, dragcanvas.height)
        },

        // Handle the image upload and attach it to the car on right with correct preview
        imageUploaded (event) {

            const reader = new FileReader();
            var self = this;
            this.content = event.target.files[0]
            var extension =   this.content.type
            var checkVideo = extension.includes("video");

            //if media is video set size limit to 25MB
            var FileSize = this.content.size / 1024 / 1024; // in MB
            if (FileSize > 25 && checkVideo ) {
                    event.preventDefault();
                    toast.errorMessage("Maximum allowed video size is 25MB!")
                    return;
              }
            //if media is image set size limit to 10MB
            if (FileSize > 10 && !checkVideo ) {
                event.preventDefault();
                toast.errorMessage("Maximum allowed image size is 10MB!")
                return;
            }

            //check if media to be uploaded is video and get screenshot

            reader.onload = function(e) {
                if(checkVideo){

                    var canvas = document.getElementById("prevImgCanvas")
                    var dragcanvas = document.getElementById("dragImgCanvas")
                    canvas.width = dragcanvas.width = '150'
                    canvas.height = dragcanvas.height ='100'
                    var video = document.getElementById("videoPreviewImg")

                    video.addEventListener('loadeddata', function() {
                        self.clearCanvas()
                       self.reloadRandomFrame();

                    }, false)

                    video.addEventListener('seeked', function() {
                        var video = document.getElementById("videoPreviewImg")
                        var context = canvas.getContext('2d')
                        context.drawImage(video, 0,0, canvas.width, canvas.height)
                        dragcanvas.getContext('2d').drawImage(canvas, 0, 0);
                    }, false);



                    var file = e.target.result;
                    video.src = file;
                    canvas.style.opacity = 1;
                    dragcanvas.style.opacity = 1;
                    self.$refs.overflowIMG.src = '';
                    self.$refs.overflowIMG.style.opacity = 0;
                    self.$refs.dragIMG.src = '';
                     self.imgUploaded = true;
                }else{
                    self.$refs.overflowIMG.src = e.target.result;
                    self.$refs.overflowIMG.style.opacity = 1;
                    self.$refs.dragIMG.src = e.target.result;
                    self.imgUploaded = true;
                    self.clearCanvas();
                }

            }

            reader.readAsDataURL(event.target.files[0]);
        },


        // Enable adding the zipcode block by pressing comma, space or enter and deleting them on backspace when input is empty
        checkKey (event) {
            var code = event.code + ''

            if (code === 'Enter' || code === 'Comma' || code === 'Space') {
                event.preventDefault()
                this.addZIP()
            }

            if (code === 'Backspace' && (this.zipCode === '' || this.zipCode === null)) {
                this.removeZIP(this.campaign.ZIP_codes.length-1)
            }
        },
        addZIP () {
            if (this.zipCode !== null && this.zipCode !== '' && this.zipCode.length > 0) {
                this.campaign.ZIP_codes.push(parseInt(this.zipCode))
                this.zipCode = null
                this.getBoundaries()
                this.getTrafficVolume()
            }
        },

        // Get the trafic volume for the selected location and update the blue box in maps
        getTrafficVolume () {
            var url = '/get/CPM'
            var body = {data: {}}
            var self = this

            if(this.mapTypeActive === 'geofence'){
                body.data.lat = this.campaign.lat
                body.data.lon = this.campaign.lon
                body.data.radius = this.campaign.distance
            } else {
                body.data.zip_codes = this.campaign.ZIP_codes
                body.from = this.campaign.start_time ? this.campaign.start_time : null
                body.till = this.campaign.end_time ? this.campaign.end_time : null
            }

            let initInterval = setInterval(() => {
                if(document.getElementById("google-type-loading-container")){
                    clearInterval(initInterval)
                    var loadingBox = document.getElementById("google-type-loading-container")
                    var loading = document.getElementById("google-type-loading-animation")
                    loadingBox.classList.remove("fade__animation")
                    loading.classList.remove("fade__animation")
                    loadingBox.style.opacity = "0.6"
                    loading.style.opacity = "0.6"

                    self.request(url, 'POST', body).then(response=>{
                        if (response.code === 0) {
                            self.trafficVolume.minPeople = response.data[0].Min_volume
                            self.trafficVolume.maxPeople = response.data[0].Max_volume
                            self.trafficVolume.avgPeople = response.data[0].Avg_volume

                            if(response.data[0].Min_volume === null || response.data[0].Max_volume === null){
                                document.getElementById("traffic-people-to").innerHTML = ""
                            } else {
                                document.getElementById("traffic-people-to").innerHTML = "&nbsp;to&nbsp;"
                            }

                            try{
                                document.getElementById("traffic-people-min").innerHTML = self.trafficVolume.minPeople
                                document.getElementById("traffic-people-max").innerHTML = self.trafficVolume.maxPeople
                                document.getElementById("traffic-average-value").innerHTML = self.trafficVolume.avgPeople
                                loadingBox.classList.add("fade__animation")
                                loading.classList.add("fade__animation")
                            } catch (error) {

                            }
                        } else {
                            if (response.code === 112) {
                                self.checkTokenConditions(self.getTrafficVolume)
                            }
                        }
                    })
                } else {
                }
            }, 50)
        },

        // Draw circle from the response for the chosen zip codes
        getBoundaries () {
            let self = this
            var url = '/get/ZIP_codes'
            var body = {
                data: { zip_codes: this.campaign.ZIP_codes }
            }

            this.request(url, 'POST', body).then(response=>{
                if (response.code === 0) {
                    this.polygons = []
                    if(response.data.boundary === null || response.data.zip_codes.length === 0){
                        return
                    }
                    let coordinates = response.data.boundary.coordinates;
                    this.bounds = new this.google.maps.LatLngBounds()

                    this.extractCoordinates(coordinates)
                    this.map.fitBounds(this.bounds)
                } else {
                    toast.errorMessage(response.message)
                    if (response.code === 112) {
                        this.checkTokenConditions(this.getBoundaries)
                    }
                }
            })
        },

        // Filter a given array of coordinates to create array suitable for drawing polygon
        extractCoordinates (coordinates, loop=false) {
            if(!loop){
                this.polygons = []
            }

            if(typeof coordinates[0][0] === "number") {
                var polygon = []
                coordinates.map(pair=>{
                    let latLng = new this.google.maps.LatLng(pair[1], pair[0])
                    this.bounds.extend(latLng)
                    polygon.push({ lat: pair[1], lng: pair[0] })
                })
                this.polygons.push(polygon)
            } else {
                coordinates.map(pair=>{
                    this.extractCoordinates(pair, true)
                })
            }
        },
        removeZIP (zip) {
            const index = this.campaign.ZIP_codes.indexOf(zip)
            this.campaign.ZIP_codes.splice(index, 1)
            this.getBoundaries()
            this.getTrafficVolume()
        },
        dragEnter () {
            this.drag = true
        },
        dragLeave () {
            this.drag = false
        },

        // Get the location for picked address from input and setup the traffic for it
        addressChanged (ev) {
            var url = '/get/ZIP_codes'
            let lat = ev.geometry.location.lat()
            let lng = ev.geometry.location.lng()

            this.campaign.lat = lat
            this.campaign.lon = lng
            this.campaign.address = ev.formatted_address

            var body = {
                data: {
                    lat,
                    lng,
                    radius: this.campaign.distance
                }
            }
            this.getTrafficVolume()
            this.request(url, 'POST', body).then(response=>{
                if (response.code === 0) {
                    this.campaign.distance = response.boundary || 10000
                    this.map.panTo({ lat, lng })
                } else {
                    toast.errorMessage(response.message)
                    if (response.code === 112) {
                        this.checkTokenConditions(this.addressChanged)
                    }
                }
            })
        },

        // Change the location address in the input field or get zip codes for clicked area & display polygon
        mapClicked (ev) {
            var lat = ev.latLng.lat()
            var lng = ev.latLng.lng()

            if(this.mapTypeActive === 'geofence'){
                this.campaign.lat = lat
                this.campaign.lon = lng

                reverseGeocode(geocoder, ev.latLng).then(res => {
                    this.campaign.address = res.formatted_address
                    this.getTrafficVolume()
                })
            } else {
                this.setupZIP(lat, lng, 10)
            }
        },

        // Get the zipcodes for the selected location and display them as polygon
        setupZIP(lat, lng, radius=null) {
                var url = '/get/ZIP_codes'
                var body = {
                    data: {
                        lat,
                        lon: lng,
                        radius: radius || this.campaign.distance
                    }
                }
                this.request(url, 'POST', body).then(response=>{
                    if (response.code === 0) {
                        response.data.zip_codes.forEach(zip=>{
                            this.campaign.ZIP_codes.push(zip)
                        })
                        this.getBoundaries()
                        this.getTrafficVolume()
                    } else {
                        toast.errorMessage(response.message)
                        if (response.code === 112) {
                            this.checkTokenConditions(this.setupZIP)
                        }
                    }
                })
        },

        // Triggered via the zipcode/geofence buttons, redraws map figures
        mapTypeChange (type) {
            this.mapTypeActive = type
            if(type === 'geofence'){
                this.campaign.ZIP_codes = []
                this.polygons = []
                this.zipCode = null
                this.setCircleListeners(true)
            } else {
                this.setupZIP(this.campaign.lat, this.campaign.lon)
            }
        },
        startChanged (startTime) {
            this.campaign.start_time = startTime
        },
        endChanged (endTime) {
            this.campaign.end_time = endTime
        },

        // Get the campaign details if we're in edit mode
        getCampaign (id) {
            var url = '/get/campaign'
            var body = {
                data: {
                    campaign_id: id
                }
            }

            this.request(url, 'POST', body).then(response=>{
                if (response.code === 0) {
                    this.campaign = response.data[0]
                    this.campaign.campaign_id = id

                    // Load the image into the upload container and the demo car
                    this.$refs.overflowIMG.src = this.campaign.content_url;
                    this.$refs.overflowIMG.style.opacity = 1;
                    this.$refs.dragIMG.src = this.campaign.content_url;

                    // Setup the dates to proper format
                    let userAgentString = navigator.userAgent;
                    let firefoxAgent =	userAgentString.indexOf("Firefox") > -1;
                    let safariAgent =  userAgentString.indexOf("Safari") > -1;
                    if(firefoxAgent || safariAgent){
                        this.campaign.start_date = new Date(moment(this.campaign.start_date).format('YYYY/MM/DD'))
                        this.campaign.end_date = new Date(moment(this.campaign.end_date).format('YYYY/MM/DD'))
                    }else{
                        this.campaign.start_date = new Date(this.customUSFormatter(this.campaign.start_date))
                        this.campaign.end_date = new Date(this.customUSFormatter(this.campaign.end_date))
                    }

                    this.campaign.start_time = this.createDateFromTime(this.campaign.start_time)
                    this.campaign.end_time = this.createDateFromTime(this.campaign.end_time)

                    // If campaign is defined with zip then get the boundaries and set proper zoom to map
                    if(this.campaign.ZIP_codes.length > 0) {
                        this.mapTypeActive = 'zipcode'
                        this.getBoundaries();
                    }

                    if(this.campaign.show_limit){
                        this.calculateTotals('show_limit')
                    } else {
                        this.calculateTotals('spend_limit')
                    }

                    this.getTrafficVolume()

                    // Setup the footer values
                    // this.calculateTotals();
                } else {
                    toast.errorMessage(response.message)
                    if (response.code === 112) {
                        this.checkTokenConditions(this.getCampaign)
                    }
                }
            })
        },

        createDateFromTime (timeString) {
            let hours = timeString.split(":")[0]
            let minutes = timeString.split(":")[1]
            let year = new Date().getFullYear()
            let month = new Date().getMonth()
            let day = new Date().getDay()

            return new Date(year, month, day, hours, minutes)
        },

        // Get the number of campaigns for the user to init the header sentence
        getUserCampaigns () {
            var url = '/get/campaign/list'
            this.request(url, 'POST', {}).then(response=>{
                if (response.code === 0) {
                    this.campaigns = response.data
                } else {
                    toast.errorMessage(response)
                    if (response.code === 112) {
                        this.checkTokenConditions(this.getUserCampaigns)
                    }
                }
            })
        },

        // Adds additional listeners to the map circle, since the embeded ones from library are not always correct
        setCircleListeners (resizeOnInit=false) {
            let self = this;

            // Wait until the circle initializes and then setup listeners for it
            let interval = setInterval(() => {
                if(self.$refs.mapCircle.$circleObject){
                    let circle = self.$refs.mapCircle.$circleObject

                    if(resizeOnInit) {
                        self.map.fitBounds(circle.getBounds())
                    }

                    // Allow map to be repositioned only when user stops dragging the circle
                    let dragging = false;
                    self.google.maps.event.addListener(circle, 'mousedown', function() {
                        dragging = true
                    })
                    self.google.maps.event.addListener(circle, 'mouseup', function() {
                        dragging = false
                    })


                    // Get the adress from the new location and get the traffic volume
                    self.google.maps.event.addListener(circle, 'center_changed', function(ev) {
                        let center = circle.getCenter()

                        if(!dragging && (self.lastCenter.lat !== center.lat()) && (self.lastCenter.lng = center.lng())){
                            self.campaign.lat = center.lat()
                            self.campaign.lon = center.lng()
                            self.lastCenter.lat = center.lat()
                            self.lastCenter.lng = center.lng()

                            let geocoderInterval = setInterval(() => {
                                if(geocoder){
                                    reverseGeocode(geocoder, {lat: center.lat(), lng: center.lng()}).then(res => {
                                        self.campaign.address = res.formatted_address
                                        self.getTrafficVolume()
                                    })
                                    clearInterval(geocoderInterval)
                                }
                            }, 50)
                        }
                    })

                    self.google.maps.event.addListener(circle, 'radius_changed', function(ev) {
                        self.campaign.distance = circle.getRadius()
                        self.map.fitBounds(circle.getBounds())
                        self.getTrafficVolume()
                    })
                    clearInterval(interval)
                }
            }, 50)
        },
        checkTokenConditions (callback) {
            try{
                var expiresDate = new Date(this.token.expires)
            } catch (err) {
                this.$router.push('/login')
            }
            var now = new Date()
            if (expiresDate <= now) {
                this.store.commit('setToken', {})
                this.$router.push('/login')
            } else {
                var body = {
                code: '',
                refresh_token: this.token.refresh_token
                }
                this.request('/refresh/token', 'POST', body).then((response) => {
                if (response.code === 0 ) {
                    store.commit('setToken', response.data[0])
                    store.commit('setDriver', response.data[0].driver)
                    callback()
                } else if (response.code === 112) {
                    this.store.commit('setToken', {})
                    this.$router.push('/login')
                }
                }).catch((error) => {
                })
            }
            var token = this.token
            token.redirect = false
            this.store.commit('setToken', token)
        }
    },
    created () {
        // Init this variable so we can compare if user has made any changes
        this.initialCampaign = Object.assign({}, this.campaign)
    },
    computed: {
        google: gmapApi,
        categories: function() {
            return (this.schema.categories || []).filter((category, index)=>{
                return index < 8 || this.allCategories
            })
        },
        ...mapGetters({
            schema: 'getSchema',
            user: 'getUser',
            token: 'getToken'
        }),
        nthCampaign: function () {
            if(!this.campaigns){
                return null
            } else {
                let len = this.campaigns.length
                let lenString = len.toString()
                let lastChar = lenString[lenString.length-1]

                if(len === 9 || len === 10 || len === 11 || len === 12) { // Escape the rules since they don't apply to these numbers
                    return len + 1 + "th"
                } else if(lastChar === "0"){
                    return len + 1 + "st"
                } else if(lastChar === "1"){
                    return len + 1 + "nd"
                } else if(lastChar === "2"){
                    return len + 1 + "rd"
                } else {
                    return len + 1 + "th"
                }

            }
        }
    },
    mounted() {
        let self = this;
        this.getUserCampaigns();
        if(this.$route.params.id) {
            this.getCampaign(this.$route.params.id)
        }
        initStickyTotals()

        // Sets up the custom elements over the google maps after its initialization
        this.$refs.mapRef.$mapPromise.then((map) => {
            self.map = map
            // var centerControlDiv = InitTypeControl(self.mapTypeActive, this.mapTypeChange)
            var zoomControlDiv = document.createElement('div')
            // zoomControlDiv.appendChild(centerControlDiv)

            self.setCircleListeners()
            InitZoomControl(zoomControlDiv, map, this.google)

            let trafficVolumePosition = this.isMobile() ? 'TOP_LEFT' : 'RIGHT_BOTTOM'
            InitTrafficVolume(map, this.google, trafficVolumePosition)
            if(!self.$route.params.id){
                self.getTrafficVolume()
            }
        })



    },
    watch: {
        google: function (val) {
            // eslint-disable-next-line no-undef
            geocoder = new google.maps.Geocoder()
        }
    },
    components: {
        SmallCar,
        MediumCar,
        LuxuryCar,
        Truck,
        VueSlider,
        Datepicker,
        TimePicker,
        SideMenuHeader,
        ClipLoader,
        GeofenceIcon,
        ZipcodeIcon
    }
}
</script>

<style>

</style>

<style scoped>
.campaign__container{
    width: 100%;
    height: 100%;
    margin: 0;
    overflow-y: hidden;
    text-align: left;
    box-sizing: border-box;
    position: relative;
    padding: 20px
}

.campaign__header{
    font-weight: normal;
    font-size: 28px;
    width: 100%;
    opacity: 0;
    animation: show__header 1s forwards;
}

.first__time__header{
    animation-name: show__header;
    animation-duration: .5s;
    animation-fill-mode: forwards;
}

@keyframes show__header {
    0%{
        opacity: 0;
    }
    50%{
        opacity: .6;
    }
    100%{
        opacity: 1;
    }
}

.campaign__body{
    flex-grow: 1;
    padding: 0 20px 30px 20px;
    height: 100%;
    overflow-y: auto;
    position: relative;
}

.campaign__info{
    font-weight: normal;
    font-size: 22px;
    margin-top: 30px;
    margin-bottom: 20px;
}

.pair__label{
    font-size: 12px;
    color: #191919;
}

.row{
    display: flex;
}

.pair{
    display: flex;
    flex-direction: column;
}

.basic__info{
    justify-content: space-between;
    max-width: calc( 65vw - 250px);
    /* width: 100%; */
}

.basic__info .pair{
    width: 45%;
}

.basic__info input{
    margin-top: 10px;
}

.error {
    font-size: 10px;
    color: #DD4242;
    margin-top: 5px;
    margin-bottom: 0;
    padding-bottom: 0;
}

input, select{
    border: 1px solid #CCD4E0;
    border-radius: 5px;
    box-shadow: 0px 0px 6px rgba(81, 95, 152, 0.161);
    height: 35px;
    box-sizing: border-box;
    padding-left: 10px;
}

.campaign__category{
    margin-top: 25px;
    max-width: 100%;
}

.campaign__category .pair__label{
    margin-bottom: 10px;
    display: block;
}

.campaign__category .category__list{
    display: flex;
    flex-wrap: wrap;
}

.campaign__category .category__list p{
    border: 1px solid #2E2E2E;
    border-radius: 15px;
    font-size: 12px;
    padding: 5px 10px 5px 10px;
    margin: 0 10px 5px 0;
    cursor: pointer;
}

.campaign__category .category__list .all__categories {
    color: #2EA3FF;
    border: none;
    border-radius: none;
}

.campaign__category .category__list p:hover{
    box-shadow: 0px 3px 6px rgba(81, 95, 152, 0.25);
}

.campaign__category .category__list .all__categories:hover{
    box-shadow: none;
}

.campaign__category .category__list .selected{
    background: #2EA3FF;
    color: #fff;
    border: 1px solid transparent;
}

.budget__limit{
    margin-top: 20px;
}

.budget__limit .budget__holder{
    display: flex;
}

.budget__limit .pair{
    margin-right: 10px;
}

.budget__limit .pair__label{
    margin-bottom: 10px;
    display: block;
}

.budget__limit .pair label{
    margin-bottom: 5px;
}

.budget__limit .input__row{
    align-items: center;
    margin-top: 10px;
}

.budget__limit p{
    font-size: 12px;
    margin-top: auto;
    margin-bottom: 10px;
    margin-right: 10px;
}

.budget__limit .labels__row{
    margin-top: 15px;
}

.budget__limit .labels__row label:first-of-type{
    width: 250px;
    display: inline-block;
}

.budget__limit .input__row p{
    text-align: center;
    width: 50px;
    margin: 0;
    padding: 0;
}

.budget__limit .input__row input{
    width: 200px;
}

.schedule__row .pair{
    width: 150px;
    margin-right: 20px;
}

.schedule__row .pair:nth-of-type(2){
    margin-right: 50px;
}

.schedule__row .pair input{
    margin-top: 10px;
}

.location__details {
    display: flex;
    width: 40%;
    margin-right: 30px;
    flex-direction: column;
}

.location__details input{
    margin-top: 5px;
}

.location__details .upload__label{
    font-size: 12px;
}

.location__details p{
    font-size: 12px;
}

.location__map {
    margin-left: 17px;
}

.location__row .location__map{
    width: 50%;
}

.location__details .upload__label {
    margin-top: 15px;
    margin-bottom: 15px;
    background: #2EA3FF;
    border-radius: 5px;
    border: none;
    padding: 8px;
    box-sizing: border-box;
    color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width:60%;
}

.location__details .upload__label svg {
    margin-right: 10px;
}

.car__type__list{
    display: flex;
}

.car__type__list .active{
    color: #fff;
    background: #2EA3FF;
    border: 1px solid transparent;
}

.car__type__item{
    border-radius: 5px;
    padding: 10px;
    border: 1px solid #2E2E2E;
    margin-right: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
}

.car__type__item svg{
    width: 50px;
    height: 35px;
}

.car__type__item p{
    padding: 0;
    margin: 10px 0 0 0;
    user-select: none;
    font-size: 12px;
}

.car__type__list .select__all{
    display: flex;
    align-items: center;
    justify-content: center;
}

.car__type__list .select__all p{
    margin: 0;
}

.add__campaign__creative{
   margin-bottom: 100px;
}

.add__campaign__creative .info__holder {
    width: 30%;
    padding: 20px 20px 20px 0;
}

.add__campaign__creative .info__holder .upload__container {
    border: 2px dashed #C7C9D2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px;
    margin-bottom: 10px;
    position: relative;
}

.add__campaign__creative .info__holder .upload__container button {
    color: white;
    background: #2EA3FF;
    border-radius: 5px;
    border: none;
    box-sizing: border-box;
    padding: 10px 20px 10px 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-top: 20px;
}

.upload__container .drag__img{
    width: 100%;
    height: 75px;
    text-align: center;
}

.add__campaign__creative .info__holder .upload__container img {
    max-width: 100%;
    max-height: 100%;
}

.add__campaign__creative .info__container {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
}

.add__campaign__creative .info__container div{
    display: flex;
    flex-direction: column;
}

.add__campaign__creative .info__container div label{
    color: #4E4E4E;
    font-size: 11px;
}

.add__campaign__creative .info__container div:first-of-type{
    text-align:left;
}

.add__campaign__creative .info__container div:nth-of-type(2){
    text-align:right;
}

.add__campaign__creative .preview__holder {
    width: 70%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.add__campaign__creative .preview__holder img:first-of-type{
    width: 90%;
    height: 80%;
}

.add__campaign__creative .preview__holder div{
    position: relative;
}

.overflow__img, #prevImgCanvas {
    position: absolute;
    width: 25%;
    max-height: 25%;
    object-fit: contain;
    bottom: 28%;
    left: 31%;
    opacity: 0;
}
#dragImgCanvas{
      position: absolute;
    width: 36%;
    max-height: 35%;
    -o-object-fit: contain;
    object-fit: contain;
    top: 10%;
    left: 32%;
     opacity: 0;
}


.slider__container{
    display: flex;
}

.vue-slider{
    width: 90% !important;
    margin-left: 10px;
    margin-right: 10px;
}

.slider__container p{
    padding: 0;
    margin: 0 5px 0 5px;
    font-size: 12px;
}

.campaign__creative{
    display: flex;
    position: absolute;
    bottom: 0px;
    left: 0px;
    margin: 15px;
    z-index: 99;
    width: calc( 100% - 30px );
}

.campaign__creative .total__budget, .campaign__creative .total__impressions, .campaign__creative .buttons{
    background: #8FCCF9;
    color: #2E2E2E
}

.campaign__creative p, .campaign__creative h3{
    margin: 0;
    padding: 0;
}

.campaign__creative .total__budget{
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    width: 30%;
    padding: 15px 15px 15px 20px;
}

.campaign__creative p{
    font-size: 14px;
}

.campaign__creative h3{
    font-size: 24px;
    font-weight: normal;
}

.campaign__creative .total__impressions{
    width: 30%;
    margin: 0 2px 0 2px;
    padding: 15px 15px 15px 20px;
}

.campaign__creative .buttons{
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    display: flex;
    align-items: center;
    width: 40%;
}

.campaign__creative .buttons button{
    border: 1px solid #2E2E2E;
    padding: 20px;
    color: #fff;
    background: #2E2E2E;
    border-radius: 5px;
    margin: 5px;
    width: 65%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.campaign__creative .buttons button .v-spinner{
    margin-right: 10px;
}

.campaign__creative .buttons button:first-of-type{
    background: transparent;
    color: #2E2E2E;
    width:35%;
}

.zip__input{
    border: 1px solid #CCD4E0;
    border-radius: 5px;
    box-shadow: 0px 0px 6px rgba(81, 95, 152, 0.161);
    padding: 0;
    display: flex;
    background: #fff;
    flex-wrap: wrap;
}

.zip__input p{
    padding: 2px 5px 2px 5px;
    margin: 2px 0 2px 5px;
    border-radius: 5px;
    border: 1px solid #000;
    font-size: 14px;
    display: flex;
    align-items: center;
    cursor: pointer;
}

.zip__input p svg{
    margin-left: 5px;
    width: 14px;
    height: 14px;
}

.zip__input input{
    border: none;
    box-shadow: none;
    margin: 0;
    flex-grow: 1;
    padding-top: 0;
    padding-bottom: 0;
    font-size: 14px;
}

.zip__input input:disabled {
    background: rgb(235, 235, 228);
}

.zip__input input {
    outline: none;
}

.drag__enter{
    background: #e7e7e7;
}

.campaign__container .header__container .logged__user {
    padding-right: 35px;
}

.time__section {
    display: flex;
}

.calendar__section {
    display: flex;
}

@media only screen and (max-width: 1500px) {
    .header__car {
        display: none;
    }
}

@media only screen and (max-width: 1200px) {
    .basic__info .pair{
        width: 95%;
    }

    .time__section {
        display: block;
        width: 50%;
    }

    .calendar__section {
        display: block;
        width: 50%;
    }

    .schedule__row .pair {
        width: 95%;
    }

    .budget__limit .pair {
        width: 95%;
    }



}

@media only screen and (max-width: 800px) {

    .budget__limit .budget__holder{
        display: block;
    }

    .campaign__location > div {
        display: block;
    }

    .location__details{
        width: 100%;
    }

    .location__map{
        width: 100% !important;
        margin-left: 0px;
    }

    .basic__info {
        display: block;
        max-width: 100% !important;
    }

    .campaign__creative .buttons button{
        width: 51%;
    }

  /*  .campaign__creative{
        bottom: -12px;
    } */

    .add__campaign__creative > div {
        display: block;

    }

    .info__holder{
        width: 100% !important;
    }

    .preview__holder{
        width: 100% !important;
    }
}

@media only screen and (max-width: 600px) {

    .campaign__container{
        overflow-y: hidden;
    }

    .row {
        display: block;
    }

    .time__section{
        width: 100%;
    }

    .calendar__section{
        width: 100%;
    }

    .campaign__body{
        margin-top: 0;
    }

    .header__car{
        display: none;
    }

    .campaign__category, .basic__info {
        width: 100%;
        max-width: 100%;
    }

    .basic__info .pair {
        width: 100%;
    }

    .schedule__row{
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .schedule__row .pair{
        margin-bottom: 10px;
        width: 100%;
        margin-right: 0 !important;
    }

    .location__row{
        flex-direction: column;
    }

    .location__row .location__details{
        width: 100%;
    }

    .location__row .location__details p{
        text-align: center;
    }

    .location__row .location__details .upload__label{
        width: 100%;
    }

    .location__row .location__details .upload__label__note{
        font-size: 10px;
    }

    .location__map {
        margin-left: 0px;
    }

    .location__row .location__map{
        margin-top: 25px;
        width: 100%;
    }

    .zip__input{
        margin-top: 5px;
    }

    .campaign__creative {
        flex-wrap: wrap;
        width: calc( 100vw - 30px );
         z-index: 8 !important;
    }

    .campaign__creative .total__budget, .campaign__creative .total__impressions{
        display: inline-block;
        flex-grow: 1;
        border-radius: 5px;
        padding: 5px 5px 5px 10px
    }

    .campaign__creative h3{
        font-size: 16px;
    }

    .campaign__creative p{
        font-size: 12px;
        margin-bottom: 5px;
    }

    .campaign__creative .buttons {
        margin-top: 2px;
        border-radius: 5px;
        padding: 5px;
        justify-content: space-between;
        width: 100%;
    }

    .campaign__creative .buttons button {
        flex-grow: 1;
        padding: 10px;
    }

    .el-date-editor.el-input{
        width: 100% !important;
    }

    .car__type__list{
        flex-wrap: wrap;
    }

    .car__type__item:last-of-type{
        margin-top: 10px;
        width: 100%;
    }

    .campaign__info{
        font-size: 18px;
    }

    .add__campaign__creative{
        margin-bottom: 150px;
    }

    .add__campaign__creative .row{
        flex-direction: column-reverse;
    }

    .add__campaign__creative .upload__container{
        padding: 15px;
    }

    .add__campaign__creative .info__holder, .add__campaign__creative .preview__holder {
        width: 100%;
    }

    .add__campaign__creative .preview__holder{
        margin-top: 20px;
        margin-bottom: 20px;
    }

    .budget__limit .budget__holder{
        flex-direction: column;
    }

    .budget__limit .budget__holder p{
        text-align: center;
        margin-top: 10px;
        margin-bottom: 10px;
        margin-right: 0;
    }

    .budget__limit .budget__holder .pair{
        width: 100%;
        margin-right: 0;
    }
    .campaign__creative{
        margin-bottom: 30px;
    }
}
@media only screen and (max-width: 400px) {
  .el-date-editor.el-input{
        width: 100% !important;
    }
    .google-type-holder{
        display: inline-block;
    }
}
</style>

<style>
.vdp-datepicker input, .vdp-datepicker select{
    border: 1px solid #CCD4E0;
    border-radius: 5px;
    box-shadow: 0px 0px 6px rgba(81, 95, 152, 0.161);
    height: 35px;
    box-sizing: border-box;
    padding-left: 10px;
    width: 100%;
    margin-top: 10px;
}
</style>
